/**
 * BreakdownPatternBanner — Directive 12 · Vector Embedding Pattern Detection.
 *
 * Polls `/api/beco/pattern-alert` for the current user's latest
 * un-acknowledged breakdown-pattern warning (raised server-side when a
 * trader's 128-D trade-sequence embedding clusters near a known
 * high-risk cohort). Dismiss calls the ack endpoint so it won't re-fire
 * for 24h even before the underlying pattern clears.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertOctagon, X } from "lucide-react";

export default function BreakdownPatternBanner() {
    const [alert, setAlert] = useState(null);
    const [dismissing, setDismissing] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchAlert = () => {
            api.get("/beco/pattern-alert")
                .then((r) => { if (!cancelled) setAlert(r.data?.alert || null); })
                .catch(() => { if (!cancelled) setAlert(null); });
        };
        fetchAlert();
        const id = setInterval(fetchAlert, 60000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (!alert) return null;

    const dismiss = async () => {
        setDismissing(true);
        try {
            await api.post(`/beco/pattern-alert/${alert.id}/ack`);
        } catch {
            /* best-effort — hide locally regardless */
        }
        setAlert(null);
    };

    return (
        <div
            className="sticky top-14 z-10 border-b border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-200 backdrop-blur-sm"
            data-testid="breakdown-pattern-banner"
            data-similarity={alert.similarity}
        >
            <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 py-2 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="p-1.5 rounded-lg bg-background/40 shrink-0">
                        <AlertOctagon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-xs font-bold tracking-tight">
                            Behavioral Pattern Warning
                        </div>
                        <div className="text-[11px] opacity-90" data-testid="breakdown-pattern-message">
                            {alert.message}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        onClick={dismiss}
                        disabled={dismissing}
                        className="p-1.5 rounded-md hover:bg-background/40 transition-colors disabled:opacity-50"
                        aria-label="Dismiss"
                        data-testid="breakdown-pattern-dismiss-btn"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
